/**
 * BW20-D — meal-level nutrient distribution scoring + "Rich in X" auto-labels.
 *
 * Lifesum's labels reflect generic balance heuristics. Haus's labels reflect
 * plant-forward endurance literature with bioavailability factors applied:
 *   - Non-heme iron absorption ≈ 18% of total iron for plant_forward, vs ≈ 25%
 *     for omnivore (Hurrell + Egli 2010, Am J Clin Nutr).
 *   - ALA → EPA conversion ≈ 5% (Brenna et al 2009).
 *   - Calcium absorption ≈ 60% from oxalate-rich plant sources (spinach,
 *     beet greens) vs ≈ 100% from low-oxalate plant + dairy (Weaver 1999).
 *
 * Distribution wording: "OFF_BALANCE", never "imbalanced". The distribution
 * is descriptive, not pejorative — a recovery-day dinner heavy on carbs is
 * off-balance vs the per-meal target band but it is not "wrong".
 */

import type { DietPattern, MealTypeTarget } from "./meal-targets";

export type MealLabelKind =
  | "HIGH_IN_PROTEIN"
  | "HIGH_IN_CARBS"
  | "HEALTHY_FAT_LEVEL"
  | "RICH_IN_FIBER"
  | "RICH_IN_IRON"
  | "RICH_IN_OMEGA3"
  | "RICH_IN_B12"
  | "HIGH_SODIUM";

export type MealLabelTone = "positive" | "neutral_warn";

export type MealLabel = {
  kind: MealLabelKind;
  tone: MealLabelTone;
};

export type MealLabelFacts = {
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g?: number | null;
  iron_mg?: number | null;
  omega3_epa_mg?: number | null;
  omega3_dha_mg?: number | null;
  omega3_ala_g?: number | null;
  vitamin_b12_ug?: number | null;
  sodium_mg?: number | null;
};

export type MealLabelProfile = {
  diet_pattern: DietPattern;
};

export const MEAL_LABEL_COPY: Record<MealLabelKind, string> = {
  HIGH_IN_PROTEIN: "High in protein",
  HIGH_IN_CARBS: "High in carbs",
  HEALTHY_FAT_LEVEL: "Healthy fat level",
  RICH_IN_FIBER: "Rich in fiber",
  RICH_IN_IRON: "Rich in iron",
  RICH_IN_OMEGA3: "Rich in omega-3",
  RICH_IN_B12: "Rich in B12",
  HIGH_SODIUM: "High sodium",
};

/**
 * Bioavailable iron in mg. Plant-forward eaters get ~18% absorption; omnivore
 * ~25%. Pescatarian split between the two. This is intentionally conservative
 * for plant-forward — the goal is to avoid telling a vegan endurance athlete
 * "you hit your iron target" when their absorbed dose is in fact low.
 */
function bioavailableIronMg(
  totalIronMg: number,
  diet: DietPattern,
): number {
  const absorption =
    diet === "plant_forward"
      ? 0.18
      : diet === "pescatarian"
      ? 0.21
      : 0.25;
  return totalIronMg * absorption;
}

/**
 * EPA-equivalent omega-3, in milligrams. ALA contributes via the ~5%
 * conversion path; EPA + DHA contribute directly. Plant-forward eaters
 * typically log ALA-only meals (chia, flax, walnut), so this conversion
 * is the only way they ever clear the threshold without a fortified food.
 */
function epaEquivalentMg(facts: MealLabelFacts): number {
  const epa = facts.omega3_epa_mg ?? 0;
  const dha = facts.omega3_dha_mg ?? 0;
  const alaMg = (facts.omega3_ala_g ?? 0) * 1000;
  return epa + dha + alaMg * 0.05;
}

export function deriveMealLabels(
  meal: MealLabelFacts,
  profile: MealLabelProfile,
): MealLabel[] {
  const labels: MealLabel[] = [];
  if (!meal.kcal || meal.kcal <= 0) return labels;

  const proteinPct = (meal.protein_g * 4) / meal.kcal;
  const carbsPct = (meal.carbs_g * 4) / meal.kcal;
  const fatPct = (meal.fat_g * 9) / meal.kcal;

  if (proteinPct >= 0.25) {
    labels.push({ kind: "HIGH_IN_PROTEIN", tone: "positive" });
  }
  if (carbsPct >= 0.55) {
    labels.push({ kind: "HIGH_IN_CARBS", tone: "positive" });
  }
  if (fatPct >= 0.2 && fatPct <= 0.35) {
    labels.push({ kind: "HEALTHY_FAT_LEVEL", tone: "positive" });
  }

  if ((meal.fiber_g ?? 0) >= 8) {
    labels.push({ kind: "RICH_IN_FIBER", tone: "positive" });
  }
  if (
    bioavailableIronMg(meal.iron_mg ?? 0, profile.diet_pattern) >= 3
  ) {
    labels.push({ kind: "RICH_IN_IRON", tone: "positive" });
  }
  if (epaEquivalentMg(meal) >= 250) {
    labels.push({ kind: "RICH_IN_OMEGA3", tone: "positive" });
  }
  if ((meal.vitamin_b12_ug ?? 0) >= 1.0) {
    labels.push({ kind: "RICH_IN_B12", tone: "positive" });
  }
  if ((meal.sodium_mg ?? 0) >= 800) {
    labels.push({ kind: "HIGH_SODIUM", tone: "neutral_warn" });
  }

  return labels;
}

export type MealDistribution = "BALANCED" | "OK" | "OFF_BALANCE";

/**
 * Compare each macro to the per-meal target band:
 *   - All three within +/-10% of band: BALANCED
 *   - All three within +/-25% of band: OK
 *   - Otherwise: OFF_BALANCE
 *
 * Wording: never "imbalanced". OFF_BALANCE is descriptive — a recovery-day
 * dinner can sit OFF_BALANCE relative to the daily template and still be
 * exactly the right meal for that day.
 */
export function scoreMealDistribution(
  meal: { protein_g: number; carbs_g: number; fat_g: number },
  target: MealTypeTarget,
): MealDistribution {
  const checks: Array<{
    actual: number;
    min: number | undefined;
    max: number | undefined;
  }> = [
    {
      actual: meal.protein_g,
      min: target.protein_min_g,
      max: target.protein_max_g,
    },
    {
      actual: meal.carbs_g,
      min: target.carbs_min_g,
      max: target.carbs_max_g,
    },
    {
      actual: meal.fat_g,
      min: target.fat_min_g,
      max: target.fat_max_g,
    },
  ];

  const drifts = checks
    .filter((c) => c.min != null && c.max != null)
    .map((c) => {
      const min = c.min!;
      const max = c.max!;
      if (c.actual >= min && c.actual <= max) return 0;
      if (c.actual < min) return (min - c.actual) / Math.max(1, min);
      return (c.actual - max) / Math.max(1, max);
    });

  if (drifts.length === 0) return "OK"; // not enough info for a stronger call

  const worst = Math.max(...drifts);
  if (worst <= 0.1) return "BALANCED";
  if (worst <= 0.25) return "OK";
  return "OFF_BALANCE";
}
